// @/services/command-history.service.js

import axios from 'axios'
import Vue from 'vue'
import authHeader from './auth-header'

// Use the same server configuration as your existing service
// eslint-disable-next-line no-unused-vars
let API_URL
let GPS_SERVER_URL

if (localStorage.getItem('apiServers')) {
  API_URL = JSON.parse(localStorage.getItem('apiServers')).url
} else {
  API_URL = Vue.prototype.$apiServers?.url || 'https://s1.4-rah.com'
}

if (localStorage.getItem('gpsServers')) {
  GPS_SERVER_URL = JSON.parse(localStorage.getItem('gpsServers')).server1.url
} else {
  GPS_SERVER_URL = Vue.prototype.$gpsServers?.server1?.url || 'https://s1.4-rah.com'
}

export class CommandHistoryService {
  // Get command history for a device
  // eslint-disable-next-line class-methods-use-this
  getHistory(payload) {
    const promise = axios.post(`${GPS_SERVER_URL}/device/command-history`, payload, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Get command history statistics
  // eslint-disable-next-line class-methods-use-this
  getHistoryStats(payload) {
    const promise = axios.post(`${GPS_SERVER_URL}/device/command-history/stats`, payload, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Retry a failed command
  // eslint-disable-next-line class-methods-use-this
  retryCommand(payload) {
    const promise = axios.post(`${GPS_SERVER_URL}/device/command-history/retry`, payload, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Bulk retry multiple failed commands
  // eslint-disable-next-line class-methods-use-this
  bulkRetryCommands(payload) {
    const promise = axios.post(`${GPS_SERVER_URL}/device/command-history/bulk-retry`, payload, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Export command history
  // eslint-disable-next-line class-methods-use-this
  exportHistory(payload) {
    const promise = axios.post(`${GPS_SERVER_URL}/device/command-history/export`, payload, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Download command history as CSV
  // eslint-disable-next-line class-methods-use-this
  downloadHistoryCSV(payload) {
    return new Promise((resolve, reject) => {
      axios.post(`${GPS_SERVER_URL}/device/command-history/export`,
        { ...payload, format: 'csv' },
        {
          headers: authHeader(),
          responseType: 'blob',
        })
        .then(response => {
          // Create blob for CSV file
          const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' })
          const url = window.URL.createObjectURL(blob)

          // Create download link
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `command-history-${payload.imei}.csv`)
          document.body.appendChild(link)
          link.click()

          // Cleanup
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)

          resolve({ status: 'success', message: 'فایل با موفقیت دانلود شد' })
        })
        .catch(error => {
          // console.error('Download CSV error:', error)
          reject(new Error('خطا در دانلود فایل CSV'))
        })
    })
  }

  // Get emergency numbers (moved from device service for consistency)
  // eslint-disable-next-line class-methods-use-this
  getEmergencyNumbers(payload) {
    const promise = axios.post(`${GPS_SERVER_URL}/device/emergency-numbers`, payload, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Enhanced command sending with history tracking
  // eslint-disable-next-line class-methods-use-this
  sendCommandWithHistory(payload) {
    const { imei, commandName } = payload
    const promise = axios.post(`${GPS_SERVER_URL}/device/command`, {
      imei,
      commandName,
    }, { headers: authHeader() })
    return promise.then(response => response.data)
  }

  // Get filtered command history with advanced options
  // eslint-disable-next-line class-methods-use-this
  getFilteredHistory(payload) {
    const {
      imei,
      commandType,
      status,
      fromDate,
      toDate,
      searchQuery,
      limit = 50,
      offset = 0,
    } = payload

    const params = { imei, limit, offset }

    if (commandType) params.commandType = commandType
    if (status) params.status = status
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    if (searchQuery) params.searchQuery = searchQuery

    const promise = axios.post(`${GPS_SERVER_URL}/device/command-history`, params, {
      headers: authHeader(),
    })
    return promise.then(response => response.data)
  }

  // Get command history for date range
  // eslint-disable-next-line class-methods-use-this
  getHistoryByDateRange(imei, dateRange) {
    const now = new Date()
    let fromDate

    switch (dateRange) {
      case 'today':
        fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'week':
        fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'all':
      default:
        fromDate = null
        break
    }

    const payload = { imei }
    if (fromDate) payload.fromDate = fromDate.toISOString()

    return this.getHistory(payload)
  }

  // Get command statistics for dashboard
  // eslint-disable-next-line class-methods-use-this
  getDashboardStats(imei, days = 7) {
    const payload = { imei, days }
    return this.getHistoryStats(payload)
  }

  // Get recent failed commands
  // eslint-disable-next-line class-methods-use-this
  getFailedCommands(imei, limit = 10) {
    const payload = {
      imei,
      status: 'failed',
      limit,
    }
    return this.getFilteredHistory(payload)
  }

  // Get pending commands
  // eslint-disable-next-line class-methods-use-this
  getPendingCommands(imei, limit = 10) {
    const payload = {
      imei,
      status: 'pending',
      limit,
    }
    return this.getFilteredHistory(payload)
  }

  // Get commands by type
  // eslint-disable-next-line class-methods-use-this
  getCommandsByType(imei, commandType, limit = 20) {
    const payload = {
      imei,
      commandType,
      limit,
    }
    return this.getFilteredHistory(payload)
  }

  // Export history as Excel (following your existing pattern)
  // eslint-disable-next-line class-methods-use-this
  exportHistoryExcel(payload) {
    return new Promise((resolve, reject) => {
      axios.post(`${GPS_SERVER_URL}/device/command-history/export`,
        { ...payload, format: 'excel' },
        {
          headers: authHeader(),
          responseType: 'blob',
        })
        .then(response => {
          // Create blob for Excel file
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
          const url = window.URL.createObjectURL(blob)

          // Create download link
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `command-history-${payload.imei}.xlsx`)
          document.body.appendChild(link)
          link.click()

          // Cleanup
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)

          resolve({ status: 'success', message: 'فایل Excel با موفقیت دانلود شد' })
        })
        .catch(error => {
          // console.error('Export Excel error:', error)
          reject(new Error('خطا در دانلود فایل Excel'))
        })
    })
  }

  // Helper methods for data formatting

  // Format command history for display
  // eslint-disable-next-line class-methods-use-this
  formatHistoryData(historyArray) {
    return historyArray.map(item => ({
      ...item,
      // eslint-disable-next-line no-underscore-dangle
      id: item._id || item.id,
      formattedDate: this.formatDate(item.createdAt),
      relativeTime: this.getRelativeTime(item.createdAt),
      statusText: this.getStatusText(item.status, item),
      statusClass: this.getStatusClass(item),
      replyLine: this.getReplyLine(item),
      typeText: this.getCommandTypeText(item.commandType),
      icon: this.getCommandIcon(item.commandType),
      canRetry: this.canRetry(item),
    }))
  }

  // Get command type text in Persian
  // eslint-disable-next-line class-methods-use-this
  getCommandTypeText(commandType) {
    const typeMap = {
      SOS: 'شماره اضطراری',
      SENALM: 'هشدار لرزشی',
      POWERALM: 'هشدار قطع برق',
      BATALM: 'هشدار باطری',
      ACCALM: 'هشدار سوئیچ',
      MOVING: 'هشدار جابجایی',
      SPEED: 'هشدار سرعت',
      LEVEL: 'حساسیت سنسور',
      RESET: 'راه اندازی مجدد',
      powerOff: 'قطع برق',
      powerOn: 'وصل برق',
      CENTER: 'شماره مدیر',
      SERVER: 'تنظیم سرور',
      OTHER: 'سایر',
    }
    return typeMap[commandType] || 'نامشخص'
  }

  // Get status text in Persian
  // eslint-disable-next-line class-methods-use-this
  getStatusText(status, item = {}) {
    // رکوردهای پیش از تأییدِ دستگاه (بدون sentAt) فقط یعنی «فرستاده شد»
    if (status === 'success' && !item.sentAt) return '📤 ارسال شد'
    if (status === 'sent' && item.deferred) return '⏳ در صفِ دستگاه'
    if (status === 'failed' && item.reason === 'DEVICE_OFFLINE') return '❌ آفلاین'
    if (status === 'failed' && item.reason === 'SUPERSEDED') return '↩ جایگزین شد'
    const statusMap = {
      success: '✅ تأیید شد',
      failed: '❌ انجام نشد',
      pending: '⏳ در انتظار',
      sent: '⏳ منتظرِ دستگاه',
      timeout: '⏱ بی‌پاسخ',
    }
    return statusMap[status] || status
  }

  // eslint-disable-next-line class-methods-use-this
  getStatusClass(item) {
    if (item.status === 'success' && !item.sentAt) return 'legacy'
    if (item.status === 'failed' && item.reason === 'SUPERSEDED') return 'legacy'
    return item.status
  }

  // یک خط زیرِ دستور: جوابِ دستگاه یا دلیل
  // eslint-disable-next-line class-methods-use-this
  getReplyLine(item) {
    if (item.status === 'success' && !item.sentAt) return 'پیش از این تغییر فقط ارسال ثبت می‌شد؛ نتیجه معلوم نیست'
    if (item.status === 'success') {
      const sec = item.confirmedAt && item.sentAt
        ? Math.max(1, Math.round((new Date(item.confirmedAt) - new Date(item.sentAt)) / 1000))
        : null
      const how = item.confirmedBy === 'relay' ? 'تأیید با تغییرِ رله' : 'تأیید با جوابِ دستگاه'
      const after = sec ? `، بعد از ${String(sec).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])} ثانیه` : ''
      const note = item.replyText && item.replyText !== 'انجام شد' ? ` — ${item.replyText}` : ''
      return `${how}${after}${note}`
    }
    if (item.status === 'failed' && item.reason === 'DEVICE_REJECTED') {
      return `دستگاه رد کرد: ${item.replyText || item.deviceReply || ''}`
    }
    if (item.status === 'failed' && item.reason === 'DEVICE_OFFLINE') return 'دستگاه به سرور وصل نبود؛ چیزی فرستاده نشد'
    if (item.status === 'sent' && item.deferred) return item.replyText || 'در صفِ دستگاه'
    if (item.status === 'sent') return 'به دستگاه رسید؛ منتظرِ جواب'
    return item.replyText || ''
  }

  // Get command icon
  // eslint-disable-next-line class-methods-use-this
  getCommandIcon(commandType) {
    const iconMap = {
      SOS: '🚨',
      SENALM: '🔔',
      POWERALM: '⚡',
      BATALM: '🔋',
      ACCALM: '🔑',
      MOVING: '📍',
      SPEED: '🚗',
      LEVEL: '🎯',
      RESET: '🔄',
      powerOff: '🔴',
      powerOn: '🟢',
      CENTER: '👨‍💼',
      SERVER: '🌐',
      OTHER: '⚙️',
    }
    return iconMap[commandType] || '⚙️'
  }

  // Format date to Persian
  // eslint-disable-next-line class-methods-use-this
  formatDate(dateString) {
    const date = new Date(dateString)
    return `${date.toLocaleDateString('fa-IR')} ${date.toLocaleTimeString('fa-IR')}`
  }

  // Get relative time
  // eslint-disable-next-line class-methods-use-this
  getRelativeTime(dateString) {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'همین الان'
    if (diffMins < 60) return `${diffMins} دقیقه پیش`
    if (diffHours < 24) return `${diffHours} ساعت پیش`
    if (diffDays < 7) return `${diffDays} روز پیش`

    return date.toLocaleDateString('fa-IR')
  }

  // Check if command can be retried
  // eslint-disable-next-line class-methods-use-this
  canRetry(item) {
    if (item.status === 'timeout') return true
    return item.status === 'failed' && item.reason !== 'SUPERSEDED'
            && (item.retryCount || 0) < (item.maxRetries || 3)
  }

  // Get success rate from stats
  // eslint-disable-next-line class-methods-use-this
  getSuccessRate(stats) {
    if (!stats || !stats.statusStats) return 0

    const total = stats.statusStats.reduce((sum, stat) => sum + stat.count, 0)
    // eslint-disable-next-line no-underscore-dangle
    const success = stats.statusStats.find(s => s._id === 'success')?.count || 0

    return total > 0 ? Math.round((success / total) * 100) : 0
  }

  // Validate command before sending
  // eslint-disable-next-line class-methods-use-this
  validateCommand(commandName) {
    if (!commandName || typeof commandName !== 'string') {
      throw new Error('نام دستور نمی‌تواند خالی باشد')
    }

    if (commandName.length > 200) {
      throw new Error('نام دستور نمی‌تواند بیش از 200 کاراکتر باشد')
    }

    return true
  }

  // Validate IMEI
  // eslint-disable-next-line class-methods-use-this
  validateIMEI(imei) {
    if (!imei || typeof imei !== 'string') {
      throw new Error('IMEI نمی‌تواند خالی باشد')
    }

    if (!/^\d{10,30}$/.test(imei)) {
      throw new Error('IMEI باید شامل 10 تا 30 رقم باشد')
    }

    return true
  }

  // Search in command history
  // eslint-disable-next-line class-methods-use-this
  searchInHistory(historyArray, query) {
    if (!query) return historyArray

    const searchTerm = query.toLowerCase()

    return historyArray.filter(item => item.commandName.toLowerCase().includes(searchTerm)
            || (item.response && item.response.toLowerCase().includes(searchTerm))
            || (item.error && item.error.toLowerCase().includes(searchTerm))
            || this.getCommandTypeText(item.commandType).toLowerCase().includes(searchTerm))
  }

  // Filter history by status
  // eslint-disable-next-line class-methods-use-this
  filterByStatus(historyArray, status) {
    if (!status) return historyArray
    return historyArray.filter(item => item.status === status)
  }

  // Filter history by command type
  // eslint-disable-next-line class-methods-use-this
  filterByCommandType(historyArray, commandType) {
    if (!commandType) return historyArray
    return historyArray.filter(item => item.commandType === commandType
            || item.commandName.includes(commandType))
  }

  // Sort history
  // eslint-disable-next-line class-methods-use-this
  sortHistory(historyArray, sortBy = 'createdAt', order = 'desc') {
    return [...historyArray].sort((a, b) => {
      let aVal = a[sortBy]
      let bVal = b[sortBy]

      if (sortBy === 'createdAt') {
        aVal = new Date(aVal)
        bVal = new Date(bVal)
      }

      if (order === 'desc') {
        return bVal > aVal ? 1 : -1
      }
      return aVal > bVal ? 1 : -1
    })
  }

  // Calculate statistics from history array
  // eslint-disable-next-line class-methods-use-this
  calculateStats(historyArray) {
    const total = historyArray.length
    const success = historyArray.filter(item => item.status === 'success').length
    const failed = historyArray.filter(item => item.status === 'failed').length
    const pending = historyArray.filter(item => item.status === 'pending').length

    return {
      total,
      success,
      failed,
      pending,
      successRate: total > 0 ? Math.round((success / total) * 100) : 0,
    }
  }
}

export default new CommandHistoryService()
